/**
 * Jobs and machines. Spec 002 US-7.
 *
 * A job is a unit of production work. An order has one or more of them, which
 * is the whole reason this is a separate table: a 200-shirt order with two
 * decoration methods runs as two jobs, on two machines, finishing at different
 * times. The order stays open until every job under it is done.
 *
 * Seeded here so the board is walkable before the database exists, same as the
 * rest of the fixtures driver.
 */

export type JobStage =
  | 'artwork'
  | 'print'
  | 'cure'
  | 'qc'
  | 'pack'
  | 'complete';

export type MachineType = 'embroidery' | 'screen' | 'dtf' | 'sublimation';
export type MachineState = 'available' | 'running' | 'maintenance';

export interface Machine {
  id: string;
  name: string;
  type: MachineType;
  state: MachineState;
  /** Units this machine gets through in a working day, for the load bar. */
  capacity_per_day: number;
  /**
   * False once the machine is retired.
   *
   * The board never sees a retired machine. The machines screen does, so it
   * can be brought back, which is why this is on the type rather than a filter
   * everybody has to remember.
   */
  active?: boolean;
}

export interface JobEvent {
  id: string;
  actor: string;
  at: string;
  from_stage: JobStage | null;
  to_stage: JobStage;
  note: string | null;
}

export interface Job {
  id: string;
  ref: string;
  order_ref: string;
  customer: string;
  garment: string;
  qty: number;
  method_key: string;
  machine_id: string | null;
  stage: JobStage;
  /**
   * Null until somebody schedules it. Both writers create a job undated on
   * purpose: a quote carries a lead TIME, not a date, and turning "5 to 7
   * working days" into a day needs to know when the clock starts.
   *
   * It was typed `string`, so every reader assumed one, and the board died on
   * the first real job. R-196.
   */
  due_at: string | null;
  /** Who is doing it. Null until somebody picks it up. R-200. */
  assigned_to?: string | null;
  /**
   * How many good pieces came off the run. Null until somebody counts, and
   * null is NOT zero: an uncounted job has not failed. R-203.
   */
  units_good?: number | null;
  fulfilment: 'pickup' | 'delivery';
  events: JobEvent[];
}

/**
 * The stages, and what an empty one says.
 *
 * `empty` is not decoration. The design sheet's rule is that an empty stage
 * keeps its column and says what fills it, because a blank column reads as a
 * fault and a sentence reads as a state. It is the same rule as an empty
 * screen naming who fills it (R-172). R-216.
 */
export const JOB_STAGES: {
  key: JobStage; label: string; hint: string; empty: string;
}[] = [
  { key: 'artwork', label: 'Artwork', hint: 'Proof and sign-off',
    empty: 'No artwork in progress. Jobs arrive here when an order is placed or a quote is converted.' },
  { key: 'print', label: 'Print', hint: 'On the press',
    empty: 'Nothing on a press. A job cannot come here until its proof is approved.' },
  { key: 'cure', label: 'Cure', hint: 'Through the dryer',
    empty: 'Nothing curing. Jobs arrive here off the press.' },
  { key: 'qc', label: 'QC', hint: 'Checked and counted',
    empty: 'Nothing to check. This is where the good pieces are counted.' },
  { key: 'pack', label: 'Pack', hint: 'Bagged and ready',
    empty: 'Nothing packing. Jobs arrive here after QC passes.' },
];

/**
 * Which machine types can run which decoration method. Enforced on assignment
 * so an embroidery job can never land on the DTF printer.
 */
export const METHOD_TO_MACHINE: Record<string, MachineType> = {
  embroidery: 'embroidery',
  screen: 'screen',
  dtf: 'dtf',
  sublimation: 'sublimation',
};

export const SEED_MACHINES: Machine[] = [
  { id: 'm-head-1', name: 'Head 1', type: 'embroidery', state: 'running', capacity_per_day: 120 },
  { id: 'm-head-2', name: 'Head 2', type: 'embroidery', state: 'running', capacity_per_day: 120 },
  { id: 'm-press-1', name: 'Press 1', type: 'screen', state: 'running', capacity_per_day: 400 },
  { id: 'm-dtf-1', name: 'DTF 1', type: 'dtf', state: 'available', capacity_per_day: 90 },
  { id: 'm-sub-1', name: 'Sub 1', type: 'sublimation', state: 'maintenance', capacity_per_day: 60 },
];

/** Dates are fixed strings, not computed, so screenshots stay reproducible. */
const ev = (
  id: string,
  actor: string,
  at: string,
  from: JobStage | null,
  to: JobStage,
  note: string | null = null,
): JobEvent => ({ id, actor, at, from_stage: from, to_stage: to, note });

export const SEED_JOBS: Job[] = [
  {
    id: 'j-1042', ref: 'TES-1042', order_ref: 'OG-4131', customer: "Kelly's Cafe",
    garment: 'Argent polo', qty: 24, method_key: 'embroidery', machine_id: null,
    stage: 'artwork', due_at: '2026-08-12', fulfilment: 'pickup',
    events: [ev('e1', 'Stripe', '2026-08-05T08:12:00+10:00', null, 'artwork', 'Payment cleared')],
  },
  {
    id: 'j-1043', ref: 'TES-1043', order_ref: 'OG-4132', customer: 'Redlands FC',
    garment: 'Premium tee', qty: 60, method_key: 'screen', machine_id: null,
    stage: 'artwork', due_at: '2026-08-14', fulfilment: 'delivery',
    events: [ev('e2', 'Xero', '2026-08-05T09:40:00+10:00', null, 'artwork', 'Invoiced on 14 day terms')],
  },
  {
    id: 'j-1039', ref: 'TES-1039', order_ref: 'OG-4128', customer: 'Ipswich Netball',
    garment: 'Sublimated kit', qty: 32, method_key: 'sublimation', machine_id: null,
    stage: 'artwork', due_at: '2026-08-19', fulfilment: 'pickup',
    events: [
      ev('e3', 'Stripe', '2026-08-03T14:02:00+10:00', null, 'artwork', null),
      ev('e4', 'Abdul', '2026-08-04T10:15:00+10:00', 'artwork', 'artwork', 'Proof round 1 sent'),
    ],
  },
  {
    id: 'j-1036', ref: 'TES-1036', order_ref: 'OG-4125', customer: 'Barton Plumbing',
    garment: 'Hi-vis work polo', qty: 18, method_key: 'embroidery', machine_id: null,
    stage: 'artwork', due_at: '2026-08-11', fulfilment: 'delivery',
    events: [
      ev('e5', 'Stripe', '2026-08-01T11:20:00+10:00', null, 'artwork', null),
      ev('e6', 'Abdul', '2026-08-02T09:05:00+10:00', 'artwork', 'artwork', 'Proof round 1 sent'),
      ev('e7', 'Customer', '2026-08-03T16:44:00+10:00', 'artwork', 'artwork', 'Approved by Nick Barton'),
    ],
  },
  {
    id: 'j-1034', ref: 'TES-1034', order_ref: 'OG-4123', customer: 'Logan Dental',
    garment: 'Tunic', qty: 40, method_key: 'embroidery', machine_id: 'm-head-2',
    stage: 'print', due_at: '2026-08-10', fulfilment: 'pickup',
    events: [
      ev('e8', 'Stripe', '2026-07-30T13:00:00+10:00', null, 'artwork', null),
      ev('e9', 'Abdul', '2026-08-01T08:30:00+10:00', 'artwork', 'print', 'Assigned to Head 2'),
    ],
  },
  {
    id: 'j-1035', ref: 'TES-1035', order_ref: 'OG-4124', customer: 'Hansen Auto',
    garment: 'Business shirt', qty: 12, method_key: 'dtf', machine_id: 'm-dtf-1',
    stage: 'print', due_at: '2026-08-09', fulfilment: 'delivery',
    events: [ev('e10', 'Dave', '2026-08-02T15:10:00+10:00', 'artwork', 'print', 'Assigned to DTF 1')],
  },
  {
    id: 'j-1029', ref: 'TES-1029', order_ref: 'OG-4119', customer: 'Springfield State School',
    garment: 'Premium tee', qty: 150, method_key: 'screen', machine_id: 'm-press-1',
    stage: 'print', due_at: '2026-08-08', fulfilment: 'delivery',
    events: [
      ev('e11', 'Dave', '2026-08-04T07:45:00+10:00', 'print', 'print', 'Screens burned, on Press 1'),
    ],
  },
  {
    id: 'j-1027', ref: 'TES-1027', order_ref: 'OG-4117', customer: 'Toowoomba Vets',
    garment: 'Softshell jacket', qty: 22, method_key: 'embroidery', machine_id: 'm-head-1',
    stage: 'qc', due_at: '2026-08-07', fulfilment: 'delivery',
    events: [ev('e12', 'Mel', '2026-08-05T11:30:00+10:00', 'print', 'qc', null)],
  },
  {
    id: 'j-1024', ref: 'TES-1024', order_ref: 'OG-4114', customer: 'Nolan Group',
    garment: 'Trucker cap', qty: 30, method_key: 'dtf', machine_id: 'm-dtf-1',
    stage: 'pack', due_at: '2026-08-03', fulfilment: 'delivery',
    events: [ev('e13', 'Mel', '2026-08-04T16:05:00+10:00', 'qc', 'pack', 'Boxed, awaiting label')],
  },
  {
    id: 'j-1025', ref: 'TES-1025', order_ref: 'OG-4115', customer: "Bree's Hair Studio",
    garment: 'Apron', qty: 8, method_key: 'embroidery', machine_id: 'm-head-1',
    stage: 'pack', due_at: '2026-08-08', fulfilment: 'pickup',
    events: [ev('e14', 'Mel', '2026-08-05T09:20:00+10:00', 'qc', 'pack', 'Customer notified')],
  },
  {
    id: 'j-1018', ref: 'TES-1018', order_ref: 'OG-4109', customer: 'Carter Civil',
    garment: 'Hi-vis work polo', qty: 75, method_key: 'screen', machine_id: 'm-press-1',
    stage: 'complete', due_at: '2026-08-01', fulfilment: 'delivery',
    events: [ev('e15', 'Dave', '2026-08-01T15:00:00+10:00', 'pack', 'complete', 'Lodged with Australia Post')],
  },
];

/**
 * Board "today", fixed so the overdue flags in the FIXTURES do not drift
 * between screenshots. It is not the board's idea of today: R-222.
 */
export const BOARD_TODAY = '2026-08-05';

/**
 * WHY EVERY FUNCTION BELOW TAKES A ZONE, AND NONE OF THEM DEFAULTS IT.
 *
 * Three of them forced `Australia/Sydney` for a workshop in **Brisbane**, while
 * `tenant_config.group.timezone` held `Australia/Brisbane` and `getTenant()`
 * read it into `tenant.timezone` for nobody to consume (R-232, R-289).
 *
 * Brisbane has no daylight saving and Sydney does. From the first Sunday in
 * October the two are an hour apart, and a job due at 23:30 Brisbane is already
 * tomorrow in Sydney: the board reads a day out, in the direction that says
 * "late" about work that is not.
 *
 * **The zone is required rather than defaulted.** A default is what created
 * this: `Australia/Sydney` sat in three call sites as though it were a fact
 * about the world rather than a setting, and nothing could see it was wrong.
 * A required argument makes a caller that has not thought about it a type
 * error. `one-timezone-rule` stops the string coming back.
 *
 * The caller passes `tenant.timezone`, which the root route loads for every
 * screen (R-140), so there is no screen that cannot reach it.
 */

/**
 * Today, as the workshop itself would say it: `YYYY-MM-DD`.
 *
 * The board compared every due date against the fixed fixture date above, so
 * on a live board "today" was 5 August 2026 for ever and a job due last week
 * read as on time. Nobody saw it because nothing on the board had a due date
 * at all until R-218 gave it one.
 */
export function boardToday(zone: string, now = new Date()): string {
  // `en-CA` is the shortest way to get ISO order out of Intl, and the time zone
  // is the point: a job due at 5pm on Friday in the workshop is not late at 3am
  // UTC on Saturday, which is still Friday afternoon on the floor.
  return now.toLocaleDateString('en-CA', { timeZone: zone });
}

/** The calendar day a due date falls on, in the workshop's own time zone. */
export function dueDay(due: string, zone: string): string {
  return new Date(due).toLocaleDateString('en-CA', { timeZone: zone });
}

/**
 * How a due date reads on a card: `Thu 3 Sep`.
 *
 * The card used to print `due_at.slice(5)`, which was right for the fixtures,
 * where a due date was the string `2026-08-05`. The database stores a moment,
 * so the first real job on the board showed `Due 09-03T07:00:00+00:00`. R-222.
 */
export function dueLabel(due: string, zone: string): string {
  // Assembled from parts rather than taken whole from Intl: `en-AU` renders
  // this as "Thu, 3 Sept", and "Sept" is one character wider than every other
  // month on a card eleven times a year.
  const at = new Date(due);
  const part = (opts: Intl.DateTimeFormatOptions) =>
    at.toLocaleDateString('en-AU', { timeZone: zone, ...opts });
  return `${part({ weekday: 'short' })} ${part({ day: 'numeric' })} ${part({ month: 'short' }).slice(0, 3)}`;
}

/**
 * `today` is passed in rather than computed here, on purpose.
 *
 * It used to default to `boardToday()`, so a board of forty cards asked the
 * clock forty times. Every answer was the same until one of them fell the far
 * side of midnight, and then one card on the screen disagreed with the rest.
 * The board decides what today is, once, and tells every card.
 */
export function isOverdue(job: Job, today: string, zone: string): boolean {
  // An undated job is not late. `null < today` is FALSE in JavaScript, so this
  // happened to give the right answer, and only by accident: `null > today` is
  // also false, and the next comparison somebody writes here would not be so
  // lucky. Said out loud rather than left to the coercion. R-196.
  if (!job.due_at) return false;
  return job.stage !== 'complete' && dueDay(job.due_at, zone) < today;
}

export function daysLate(job: Job, today: string, zone: string): number {
  // Zero, not NaN. `new Date(null)` is the epoch, so an undated job would have
  // read as twenty thousand days late on a card.
  if (!job.due_at) return 0;
  // Both sides as a calendar day in the workshop's zone, so the answer is a
  // count of days and not a count of hours rounded into one.
  const ms = new Date(`${today}T00:00:00Z`).getTime()
    - new Date(`${dueDay(job.due_at, zone)}T00:00:00Z`).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}
